/**
 * Paging through a PDF in the LinkedIn preview: which pages there are to reach, which arrow leads
 * where, which pages are kept ready, and where each one's picture comes from. Kept apart from the
 * component so the rules can be tested without a browser.
 */

/**
 * The auth-proxied path of one page of a PDF as a picture (`GET /assets/:id/pages/:page`), for the
 * pages after the first - the first is the asset's thumbnail. The server draws pages as they are
 * first asked for and keeps them, so the first ask of a run of pages can take a moment.
 */
export function pdfPageUrl(assetId: string, page: number): string {
  return `/api/assets/${assetId}/pages/${page}`;
}

/**
 * How many pages the preview can page through. Paging needs a count to stop at and an asset to ask
 * for pages; without both, the first page is all there is and no arrows are drawn.
 */
export function pageableCount(
  pageCount: number | null | undefined,
  assetId: string | null | undefined,
): number {
  if (!assetId || pageCount == null || !Number.isFinite(pageCount) || pageCount <= 1) return 1;
  return Math.floor(pageCount);
}

/** `page` held between the first page and the last. */
export function clampPage(page: number, count: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(Math.max(1, Math.trunc(page)), Math.max(1, count));
}

/** One of the two arrows: which side of the page it sits on, which way it steps, and its name. */
export interface PageArrow {
  side: 'left' | 'right';
  by: -1 | 1;
  label: string;
}

/**
 * The arrows, in the order a keyboard reaches them. One table, so that an arrow's side, its
 * direction and its name cannot come apart.
 */
export const PAGE_ARROWS: readonly PageArrow[] = [
  { side: 'left', by: -1, label: 'Previous page' },
  { side: 'right', by: 1, label: 'Next page' },
];

/** The page an arrow leads to from `page`. No wrapping round: the ends are the ends. */
export function pageAfter(arrow: PageArrow, page: number, count: number): number {
  return clampPage(page + arrow.by, count);
}

/**
 * Whether an arrow has anywhere to go from `page`. One that has not is taken away - as LinkedIn
 * does, not left in place greyed out.
 */
export function arrowShown(arrow: PageArrow, page: number, count: number): boolean {
  return pageAfter(arrow, page, count) !== page;
}

/**
 * Where focus goes after `pressed` has brought the reader to `page`: nowhere while that arrow is
 * still there (so it can be pressed again), and to the other arrow once it has been taken away at
 * the end it led to - LinkedIn's viewer does the same.
 */
export function arrowToFocus(pressed: PageArrow, page: number, count: number): PageArrow | null {
  if (arrowShown(pressed, page, count)) return null;
  return PAGE_ARROWS.find((arrow) => arrow !== pressed && arrowShown(arrow, page, count)) ?? null;
}

/**
 * The pages kept in the frame. Once the reader is `reaching` for the document, the page showing
 * and its neighbours: a step either way then slides in a page that is already there (and most
 * likely already loaded), and a 300-page PDF is never 300 images. Before that, only the page
 * showing - a preview nobody pages through asks the server for nothing.
 */
export function mountedPages(page: number, count: number, reaching: boolean): number[] {
  const pages = reaching ? [page - 1, page, page + 1] : [page];
  return pages.filter((n) => n >= 1 && n <= count);
}

/** A picture's own proportions as a CSS aspect-ratio; null for one that has none yet. */
export function frameShape(width: number, height: number): string | null {
  if (!(width > 0) || !(height > 0)) return null;
  return `${width} / ${height}`;
}

/**
 * The shape of the frame: one for the whole PDF, as in LinkedIn's viewer, so that neither it nor
 * the arrows move as the pages turn. That of the first page's picture; a later page's stands in
 * only while the first has none (it was never drawn, or is still on its way), and a page of any
 * other shape is fitted inside. Until any picture has loaded, a portrait slide's 4:5.
 */
export function frameShapeOf(firstPage: string | null, standIn: string | null): string {
  return firstPage ?? standIn ?? '4 / 5';
}

/**
 * How long a page that fails in front of the reader is left before it is asked for again: three
 * more asks, further apart each time. The server turns a page away while it is busy drawing
 * others, which passes in seconds; a page that still cannot be had after these is left alone.
 */
export const PAGE_RETRY_WAITS_MS: readonly number[] = [2000, 4000, 8000];

/**
 * What is done about a page that could not be loaded, while the reader is on it. `now`: it is
 * asked for again at once - it had already failed by the time the reader got to it (it was fetched
 * ahead, at the side, and "right now" may have passed). `wait`: how long until it is asked for
 * again after that - the next of {@link PAGE_RETRY_WAITS_MS} - or null once those are used up.
 * Nothing at all for a page that is not showing: nobody is waiting for it.
 */
export function retryPlan(page: {
  showing: boolean;
  /** This is the moment the reader got to it. */
  arrived: boolean;
  failed: boolean;
  /** How many times it has been asked for again, after a wait, on this visit. */
  retries: number;
}): { now: boolean; wait: number | null } {
  if (!page.showing || !page.failed) return { now: false, wait: null };
  return { now: page.arrived, wait: PAGE_RETRY_WAITS_MS[page.retries] ?? null };
}
